import { useEffect, useState, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
  ClickAwayListener,
  Divider,
  Popper,
} from "@mui/material";

import mainStyle from "./mainStyle.module.scss";

import { ReactComponent as ChevronUp } from "assets/images/icons/icn_pagination_chevron_up.svg";
import { ReactComponent as ChevronUpHover } from "assets/images/icons/icn_pagination_chevron_up_hover.svg";
import { ReactComponent as ChevronDown } from "assets/images/icons/icn_pagination_chevron_down.svg";
import { ReactComponent as ChevronDownHover } from "assets/images/icons/icn_pagination_chevron_down_hover.svg";

export const MenuSelect = ({
  value,
  onChange,
  options,
  pinningOptions,
  width,
  placeholder,
  disabled,
  borderLeftWidth,
  borderRightWidth,
  borderBottomWidth,
  isEditedField = false,
  additionZIndex = 0,
  placement = "bottom-end",
}) => {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState(null);
  const [chevronHover, setChevronHover] = useState(false);
  const [menuWidthPx, setMenuWidthPx] = useState(width);

  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuOpen = Boolean(anchorEl);

  const handleOpen = (event) => {
    if (disabled) return;
    event.stopPropagation();
    triggerRef.current = event.currentTarget;
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setChevronHover(false);
    if (triggerRef.current) triggerRef.current.focus();
  };

  useEffect(() => {
    if (containerRef.current) setMenuWidthPx(containerRef.current.offsetWidth);
  }, [width]);

  const allOptions = [...options, ...(pinningOptions || [])];
  const label = allOptions.find((opt) => opt.value === value)?.label || "";

  return (
    <Box
      ref={containerRef}
      onClick={handleOpen}
      onMouseEnter={() => !disabled && setChevronHover(true)}
      onMouseLeave={() => !disabled && setChevronHover(false)}
      sx={{
        position: "relative",
        display: "flex",
        border: isEditedField
          ? `2px solid ${theme.palette.primary.orange}`
          : `1px solid ${disabled
            ? theme.palette.primary.borderGreyPrimary
            : menuOpen
              ? theme.palette.primary.main
              : chevronHover
                ? theme.palette.primary.mainHover
                : theme.palette.primary.greyFifth
          }`,
        borderRadius: theme.shape.customBorderRadius[1],
        mr: 2,
        width: width,
        height: 29,
        bgcolor: theme.palette.primary.white,
        px: 1,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
      tabIndex={0}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          overflow: "hidden",
          flexGrow: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: theme.typography.customFontSize[2],
            color: theme.palette.primary.greyPrimary,
            fontWeight: theme.typography.customFontWeight[3],
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontFamily: "Open Sans",
          }}
        >
          {label || (
            <span style={{ color: theme.palette.primary.greyThird }}>
              {placeholder}
            </span>
          )}
        </Typography>
      </Box>

      {/* Chevron */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 32,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderTopRightRadius: 6,
          borderBottomRightRadius: 6,
          pointerEvents: "auto",
        }}
      >
        {menuOpen ? (
          chevronHover ? (
            <ChevronUpHover />
          ) : (
            <ChevronUp />
          )
        ) : chevronHover ? (
          <ChevronDownHover />
        ) : (
          <ChevronDown />
        )}
      </Box>

      {/* Popper Menu */}
      {menuOpen && (
        <Popper
          open={menuOpen}
          anchorEl={anchorEl}
          placement={placement}
          disablePortal={false}
          style={{ zIndex: theme.zIndex.modal, width: menuWidthPx }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Paper
              elevation={3}
              sx={{
                width: menuWidthPx,
                borderRadius: 3,
                boxShadow: "0px 1px 4px rgba(0,0,0,0.08)",
                zIndex: theme.zIndex.modal + additionZIndex,

                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: -8,
                  right: 16,
                  width: 0,
                  height: 0,
                  borderLeft: `${borderLeftWidth} solid transparent`,
                  borderRight: `${borderRightWidth} solid transparent`,
                  borderBottom: `${borderBottomWidth} solid #ffffff`,
                  filter:
                    "drop-shadow(-2px -1px 2px rgba(0, 0, 0, 0.05)) drop-shadow(2px -1px 2px rgba(0, 0, 0, 0.05))",
                },
              }}
            >
              <List
                className={mainStyle["custom-scrollbar-menu"]}
                sx={{
                  paddingTop: "5px",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                  boxSizing: "content-box",
                  marginRight: "10px",
                  maxHeight: "180px",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                {options.map((opt) => (
                  <ListItem
                    key={opt.label}
                    button
                    selected={value === opt.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(opt);
                      handleClose();
                    }}
                    sx={{
                      color: theme.palette.primary.main,
                      paddingLeft: "10px",
                      margin: "5px 5px 0 0",
                      borderRadius: theme.shape.customBorderRadius[0],
                      height: "40px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.lightBlue,
                      },
                      "&.Mui-selected": {
                        backgroundColor: theme.palette.primary.subBlue,
                      },
                    }}
                  >
                    <ListItemText
                      primary={opt.label}
                      primaryTypographyProps={{
                        style: {
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          fontSize: theme.typography.customFontSize[2],
                        },
                      }}
                    />
                  </ListItem>
                ))}
                {
                  pinningOptions && (
                    <>
                      <Divider sx={{ mt: 1 }} />
                      {
                        pinningOptions.map((opt) => (
                          <ListItem
                            key={opt.label}
                            button
                            selected={value === opt.value}
                            onClick={(e) => {
                              e.stopPropagation();
                              onChange(opt);
                              handleClose();
                            }}
                            sx={{
                              color: theme.palette.primary.main,
                              paddingLeft: "10px",
                              margin: "5px 5px 0 0",
                              borderRadius: theme.shape.customBorderRadius[0],
                              height: "40px",
                              "&:hover": {
                                backgroundColor: theme.palette.primary.lightBlue,
                              },
                              "&.Mui-selected": {
                                backgroundColor: theme.palette.primary.subBlue,
                              },
                            }}
                          >
                            <ListItemText
                              primary={opt.value}
                              primaryTypographyProps={{
                                style: {
                                  textOverflow: "ellipsis",
                                  overflow: "hidden",
                                  fontSize: theme.typography.customFontSize[2],
                                },
                              }}
                            />
                          </ListItem>
                        ))
                      }
                    </>
                  )
                }
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </Box>
  );
};
